import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Paper, Box, Typography, TextField, Button,
  FormControl, InputLabel, Select, MenuItem, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Chip, CircularProgress
} from '@mui/material';
import { Download, Assessment } from '@mui/icons-material';
import { workReportService } from '../services/workReportService';
import { userService } from '../services/userService';
import { kcService } from '../services/kcService';
import { toast } from 'react-toastify';

const firstOfMonth = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
};
const today = () => new Date().toISOString().split('T')[0];

const Reports = () => {
  const [filters, setFilters] = useState({ from: firstOfMonth(), to: today(), userId: '', kcId: '' });
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [kcs, setKcs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    userService.getAllUsers().then(u => setUsers(u.filter(x => x.role === 'technician' || x.role === 'admin'))).catch(() => {});
    kcService.getAllKcs().then(setKcs).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await workReportService.getSummary(filters);
      setRows(data);
    } catch (error) {
      toast.error('Auswertung konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const handleChange = (field, value) => setFilters(prev => ({ ...prev, [field]: value }));

  // pro Techniker zusammenfassen (Zwischensummen)
  const byTech = {};
  rows.forEach(r => {
    if (!byTech[r.user_id]) byTech[r.user_id] = { username: r.username, total: 0, items: [] };
    byTech[r.user_id].total += Number(r.total_hours) || 0;
    byTech[r.user_id].items.push(r);
  });
  const grandTotal = rows.reduce((s, r) => s + (Number(r.total_hours) || 0), 0);

  const handleExport = async () => {
    try {
      await workReportService.exportSummaryCsv(filters);
    } catch (error) {
      toast.error('Export fehlgeschlagen');
    }
  };

  return (
    <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} gap={2} flexWrap="wrap">
        <Typography variant="h4" sx={{ fontWeight: 700, fontSize: { xs: '1.5rem', sm: '2.125rem' }, background: 'linear-gradient(135deg, #CC0000 0%, #8B0000 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
          <Assessment sx={{ mr: 1, verticalAlign: 'middle', color: '#CC0000' }} />
          Stunden-Auswertung
        </Typography>
        <Button variant="contained" startIcon={<Download />} onClick={handleExport} disabled={rows.length === 0}>
          CSV Export
        </Button>
      </Box>

      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
        <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
          <TextField size="small" type="date" label="Von" value={filters.from} onChange={(e) => handleChange('from', e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField size="small" type="date" label="Bis" value={filters.to} onChange={(e) => handleChange('to', e.target.value)} InputLabelProps={{ shrink: true }} />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Techniker</InputLabel>
            <Select value={filters.userId} label="Techniker" onChange={(e) => handleChange('userId', e.target.value)}>
              <MenuItem value="">Alle</MenuItem>
              {users.map(u => (<MenuItem key={u.id} value={u.id}>{u.username}</MenuItem>))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>KC</InputLabel>
            <Select value={filters.kcId} label="KC" onChange={(e) => handleChange('kcId', e.target.value)}>
              <MenuItem value="">Alle</MenuItem>
              {kcs.map(kc => (<MenuItem key={kc.id} value={kc.id}>{kc.name}</MenuItem>))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      <Paper>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
        ) : rows.length === 0 ? (
          <Box p={4} textAlign="center"><Typography color="textSecondary">Keine Arbeitszeiten im gewählten Zeitraum</Typography></Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Techniker</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>KC</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Einträge</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Stunden</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.values(byTech).map((tech) => (
                  <React.Fragment key={tech.username}>
                    {tech.items.map((r, i) => (
                      <TableRow key={`${tech.username}-${r.kc_id || 'none'}-${i}`}>
                        <TableCell>{i === 0 ? <strong>{tech.username}</strong> : ''}</TableCell>
                        <TableCell>{r.kc_name || <Chip label="ohne KC" size="small" variant="outlined" />}</TableCell>
                        <TableCell align="center">{r.entries}</TableCell>
                        <TableCell align="right">{Number(r.total_hours).toFixed(2)} h</TableCell>
                      </TableRow>
                    ))}
                    {tech.items.length > 1 && (
                      <TableRow sx={{ backgroundColor: 'rgba(0,0,0,0.03)' }}>
                        <TableCell colSpan={3} sx={{ fontStyle: 'italic' }}>Summe {tech.username}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>{tech.total.toFixed(2)} h</TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
                <TableRow sx={{ backgroundColor: 'rgba(204,0,0,0.06)' }}>
                  <TableCell colSpan={3} sx={{ fontWeight: 700 }}>Gesamt</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{grandTotal.toFixed(2)} h</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Container>
  );
};

export default Reports;
